import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import FormDialog from 'components/FormDialog';
import Grid from '@material-ui/core/Grid';
import WarningIcon from '@material-ui/icons/Warning';
import ListAltIcon from '@material-ui/icons/ListAlt';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import CheckIcon from '@material-ui/icons/Check';
import CancelIcon from '@material-ui/icons/Cancel';
import LibraryAddIcon from '@material-ui/icons/LibraryAdd';
import LinearProgressWithLabel from 'components/LinearProgressWithLabel';
import Divider from '@material-ui/core/Divider';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import { cancellationReasons } from '@silvergatedelivery/constants';
import { formatDatetime } from 'utilities/format';
import { Semaphore } from 'async-mutex';
import { request } from 'utilities/graph';
import { getRecurringOrder } from 'graphql/queries';
import { createRecurringOrder, createRecurringOrderLog, adminCreateOrder, adminUpdateOrder, updateRecurringOrder } from 'graphql/mutations';
import { TIME_ZONE, WEEKDAYS_LONG } from '@silvergatedelivery/constants';
import moment from 'moment';
import cache from 'utilities/cache';

export default function RecurringOrderUpdateOrdersDialog({
  open,
  title = '訂單列表',
  toCancelOrders = [],
  toCreateOrders = [],
  elders = [],
  toCreateRecurringOrder,
  toUpdateRecurringOrder,
  toCancelOrdersByServiceEventInfo = [],
  onCancel,
  onUpdate,
  setToCreateOrders,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [orderCreated, setOrderCreated] = useState(false);
  const [createResults, setCreateResults] = useState([]);
  const [createErrors, setCreateErrors] = useState([]);
  const [cancelOrdersProgress, setCancelOrdersProgress] = useState(0);
  const [selectedSkippedDates, setSelectedSkippedDates] = useState({});

  useEffect(() => {
    setIsLoading(false);
    setOrderCreated(false);
    setCreateResults([]);
    setCreateErrors([]);
    setCancelOrdersProgress(0);
    setSelectedSkippedDates({});
  }, [toCreateRecurringOrder, toUpdateRecurringOrder]);

  const getRecurringOrderUpdated = async (recurringOrderId) => {
    const { data: { getRecurringOrder: data } } = await request(getRecurringOrder, { id: recurringOrderId });
    return data;
  };
  const cancelOrders = async () => {
    let orderProcessed = 0;
    const toUpdateData = [];
    const ordersUncancelled = toCancelOrders.filter((order) => !['completed', 'delivered', 'cancelled'].includes(order.status));
    const s = new Semaphore(10);
    const orderGrouped = {};
    ordersUncancelled.forEach((order) => {
      const groupId = order.orderGroupId || order.id;
      orderGrouped[groupId] = orderGrouped[groupId] || [];
      orderGrouped[groupId].push(order);
    });
    await Promise.all(Object.keys(orderGrouped).map((groupId) =>
      s.runExclusive(async () => {
        const data = orderGrouped[groupId].map((order) => (
          {
            id: order.id,
            restaurantId: order.restaurantId,
            deliveryBy: order.deliveryBy,
            status: 'cancelled',
            tier: order.tier,
            cancellationReason: 'others',
            cancellationNote: '重置週期性排單',
          }
        ));
        try {
          await request(adminUpdateOrder, { input: { orders: data } });
          toUpdateData.push(...data);
        } catch (e) {
          console.error(e.errors[0].message);
        }
        orderProcessed += data.length;
        setCancelOrdersProgress(orderProcessed * 100 / ordersUncancelled.length);
      }),
    ));
  };
  const handleComplete = async () => {
    if (orderCreated) {
      setOrderCreated(false);
      setIsLoading(false);
      setCreateResults([]);
      setCreateErrors([]);
      setCancelOrdersProgress(0);
      setSelectedSkippedDates({});
      onCancel && onCancel();
      return;
    }
    setIsLoading(true);
    await cancelOrders();
    let results = [];
    const errors = [];
    const elderNameInErrors = [];
    const toCancelOrdersByServiceEvent = [];
    const localToCreateOrders = [...toCreateOrders];
    if (localToCreateOrders.length !== 0) {
      localToCreateOrders.forEach((order) => {
        if (!order.skippedDatetimes || order.skippedDatetimes.length === 0) return;
        const selectedSkipped = order.skippedDatetimes
          .filter((skipped) => skipped.needToCreate)
          .map((skipped) => skipped.datetime);
        order.deliveryDatetimes = [...order.deliveryDatetimes, ...selectedSkipped];
      });
      const s = new Semaphore(10);
      await Promise.all(localToCreateOrders.filter((order) => order.deliveryDatetimes.length !== 0).map((order) =>
        s.runExclusive(async () => {
          try {
            const orderToCreate = { ...order };
            delete orderToCreate.skippedDatetimes;
            const res = await request(adminCreateOrder, { input: { orders: [orderToCreate] } });
            const { data: { adminCreateOrder: { data } } } = res;
            results = [...results, ...data];
            setCreateResults(results);
          } catch (e) {
            let message = '';
            if (e.errors && e.errors[0]) {
              switch (e.errors[0].message) {
                case 'The string supplied did not seem to be a phone number':
                  message = `熊貓外送: 送餐對象電話不完整或有誤`;
                  break;
                case 'Unable to process order\norder is outside deliverable range':
                  message = `熊貓外送: 送餐對象地址不在服務範圍內`;
                  break;
                default:
                  message = e.errors[0].message;
              }
            }
            errors.push({
              elderId: order.elderId,
              message,
            });
            const elderName = (elders.find(({ id })=> id === order.elderId) || {}).name;
            if (elderName) {
              elderNameInErrors.push(elderName);
            }
          }
        }),
      ));
      toCancelOrdersByServiceEventInfo.forEach((info) => {
        const order = results.find(({ elderId, deliveryBy }) => elderId === info.elderId && deliveryBy === info.deliveryDatetime);
        if (order) {
          const { cancellationReason, cancellationNote, restaurantId, deliveryDatetime } = info;
          toCancelOrdersByServiceEvent.push({
            id: order.id,
            restaurantId,
            deliveryBy: deliveryDatetime,
            status: 'cancelled',
            tier: 1,
            cancellationReason,
            cancellationNote,
          });
        }
      });
    }
    if (toCancelOrdersByServiceEvent.length > 0) {
      try {
        await request(adminUpdateOrder, { input: { orders: toCancelOrdersByServiceEvent } });
      } catch (e) {
        console.error(e.errors[0].message);
      }
    }
    const now = moment().toISOString();
    let recurringOrderId;
    const username = cache.get('app:username');
    if (toCreateRecurringOrder) {
      Object.assign(toCreateRecurringOrder, {
        lastExecutedAt: now,
        lastExecutedStatus: errors.length === 0 ? 'success' : 'failed',
        lastExecutedStatusMessage: elderNameInErrors.length === 0 ? '' : `新增訂單失敗: ${elderNameInErrors.join(' ')}`,
      });
      try {
        const { data: { createRecurringOrder: { id } } } = await request(createRecurringOrder, { input: toCreateRecurringOrder });
        recurringOrderId = id;
      } catch (e) {
        console.log(e);
      }
    } else if (toUpdateRecurringOrder) {
      recurringOrderId = toUpdateRecurringOrder.id;
      try {
        await request(updateRecurringOrder, { input: {
          id: toUpdateRecurringOrder.id,
          lastExecutedAt: now,
          lastExecutedStatus: errors.length === 0 ? 'success' : 'failed',
          lastExecutedStatusMessage: elderNameInErrors.length === 0 ? '' : `新增訂單失敗: ${elderNameInErrors.join(' ')}`,
          updatedAt: now,
          updatedBy: username,
        } });
      } catch (e) {
        console.log(e);
      }
    }
    try {
      const resultsGroupedByDeliveryBy = results.reduce((acc, order) => {
        const key = order.deliveryBy;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(order);
        return acc;
      }, {});
      const toCreateRecurringOrderLog = [];
      Object.keys(resultsGroupedByDeliveryBy).map((deliveryBy) => {
        toCreateRecurringOrderLog.push({
          orderIds: resultsGroupedByDeliveryBy[deliveryBy].map(({ id }) => id),
          recurringOrderId,
          deliveryBy: (toCreateRecurringOrder || toUpdateRecurringOrder).deliveryBy,
          date: moment(deliveryBy).tz(TIME_ZONE).format('YYYY-MM-DD'),
          createdAt: now,
          createdBy: username,
          updatedAt: now,
          updatedBy: username,
        });
      });
      if (toCreateRecurringOrderLog.length !== 0) {
        await Promise.all(toCreateRecurringOrderLog.map((recurringOrderLog) => {
          request(createRecurringOrderLog, { input: recurringOrderLog });
        }));
      }
    } catch (e) {
      console.log(e);
    }
    const recurringOrder = await getRecurringOrderUpdated(recurringOrderId);
    onUpdate && onUpdate(recurringOrder);
    setCreateErrors(errors);
    setOrderCreated(true);
    setIsLoading(false);
  };

  const handleSelectAll = (elderId) => (event) => {
    const order = toCreateOrders.find((o) => o.elderId === elderId);
    if (!order) return;
    const newSelectedSkippedDates = { ...selectedSkippedDates };
    newSelectedSkippedDates[elderId] = event.target.checked;
    order.skippedDatetimes.forEach((skipped) => {
      skipped.needToCreate = event.target.checked;
    });
    setSelectedSkippedDates(newSelectedSkippedDates);
    setToCreateOrders && setToCreateOrders([...toCreateOrders]);
  };

  const recurringOrder = toCreateRecurringOrder || toUpdateRecurringOrder;

  return (
    <FormDialog
      title={title}
      openOnInit={true}
      fullScreen={true}
      hideCloseButton={true}
      open={open}
    >
      <Grid container spacing={2}>
        <Grid item container spacing={1}>
          {recurringOrder &&
          <Grid item xs={12} container direction='row' alignItems='center'>
            <LibraryAddIcon style={{ marginRight: 4 }}/>
            <Typography variant="h6">
              週期性排單設定
            </Typography>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', width: '100%', marginLeft: 20, marginBottom: 20 }}>
              {recurringOrder.elderId &&
              <div style={{ marginRight: 24 }}>
                <Typography variant="subtitle1" color="textSecondary">送餐對象</Typography>
                <Typography variant="body1">
                  {(() => {
                    const elder = (elders || []).find((e) => e.id === recurringOrder.elderId);
                    return elder ? elder.name : (recurringOrder.elderName || '');
                  })()}
                </Typography>
              </div>}
              {recurringOrder.deliveryGroupId &&
              <div style={{ marginRight: 24 }}>
                <Typography variant="subtitle1" color="textSecondary">送餐群組</Typography>
                <Typography variant="body1">
                  {recurringOrder.deliveryGroupName || (recurringOrder.deliveryGroup && recurringOrder.deliveryGroup.name) || ''}
                </Typography>
              </div>}
              <div style={{ marginRight: 24 }}>
                <Typography variant="subtitle1" color="textSecondary">送餐時間</Typography>
                <Typography variant="body1">
                  {moment(recurringOrder.deliveryBy, 'HH:mm:ss').format('HH:mm')}
                </Typography>
              </div>
              <div style={{ marginRight: 24 }}>
                <Typography variant="subtitle1" color="textSecondary">開始日期</Typography>
                <Typography variant="body1">
                  {recurringOrder.startOn}
                </Typography>
              </div>
              <div style={{ marginRight: 24 }}>
                <Typography variant="subtitle1" color="textSecondary">結束日期</Typography>
                <Typography variant="body1">
                  {recurringOrder.endOn || '無'}
                </Typography>
              </div>
              <div style={{ marginRight: 24 }}>
                <Typography variant="subtitle1" color="textSecondary">週期</Typography>
                <Typography variant="body1">
                  {recurringOrder.repeatOn.sort((a, b) => {
                    if (a === 0) return 1;
                    if (b === 0) return -1;
                    return a - b;
                  }).map((day) => `${WEEKDAYS_LONG[day]}`).join('、')}
                </Typography>
              </div>
            </div>
          </Grid>}
          {toCancelOrders.length !== 0 &&
            <div style={{ marginBottom: 20 }}>
              <Grid item xs={12} container direction='row' alignItems='center'>
                <WarningIcon style={{ marginRight: 4 }}/>
                <Typography variant="h6">
                  {`此週期性排單30天內有${toCancelOrders.length}張訂單，按下確定後會取消這些訂單`}
                </Typography>
              </Grid>
              {cancelOrdersProgress !== 0 &&
              <Grid item xs={12}>
                <div style={{ width: 600 }}>
                  <LinearProgressWithLabel value={cancelOrdersProgress}/>
                </div>
              </Grid>}
            </div>}
          {toCancelOrders.length === 0 && recurringOrder?.status === '已停用' &&
            <div style={{ marginBottom: 20 }}>
              <Grid item xs={12} container direction='row' alignItems='center'>
                <WarningIcon style={{ marginRight: 4 }}/>
                <Typography variant="h6">
                  {`此週期性排單30天內沒有任何訂單`}
                </Typography>
              </Grid>
            </div>}
          {toCreateOrders.length !== 0 &&
            <Grid item xs={12} container direction='row' alignItems='center'>
              <ListAltIcon style={{ marginRight: 4 }}/>
              <Typography variant="h6">
                請檢閱後按下確定新增下列訂單
              </Typography>
            </Grid>}
          {toCreateOrders.length === 0 && recurringOrder?.status === '使用中' &&
            <Grid item xs={12} container direction='row' alignItems='center'>
              <Typography variant="h6">
                {`此週期性排單在30天內沒有需要新增的訂單，請確認送餐對象與此排單的送餐週期設定是否正確${toCreateRecurringOrder ? '，若仍要新增此週期性排單設定請按確定' : ''}。`}
              </Typography>
            </Grid>}
          {
            toCreateOrders.map((order, index) => (
              <Grid item container xs={12} key={index} spacing={1} style={{ marginLeft: 20 }}>
                <Grid item xs={12}>
                  <Typography variant="h6" component="p">
                    {(elders.find(({ id })=> id === order.elderId) || {}).name || ''}
                  </Typography>
                </Grid>
                {order.deliveryDatetimes.map((datetime) => {
                  const matched = createResults.find(({ elderId, deliveryBy }) => elderId === order.elderId && deliveryBy === datetime);
                  const matchedToCancel = toCancelOrdersByServiceEventInfo.find(
                    ({ elderId, deliveryDatetime }) => elderId === order.elderId && deliveryDatetime === datetime);
                  if (matchedToCancel) { // 不顯示因服務事件取消的訂單
                    return null;
                  }
                  return (
                    <Grid item sm={4} md={3} lg={2} container align="center" key={datetime}>
                      <Grid item xs={12} container align="center">
                        {isLoading && !matched && <CircularProgress color="primary" size={24} />}
                        {(isLoading || orderCreated) && matched && <CheckIcon color="primary" />}
                        {(!isLoading && orderCreated) && !matched && <CancelIcon color="secondary" />}
                        <Typography style={{ marginTop: 2, marginLeft: 4 }}>
                          {formatDatetime(datetime, { weekday: true })}
                        </Typography>
                      </Grid>
                    </Grid>
                  );
                })}
                {order.skippedDatetimes && order.skippedDatetimes.length > 0 && (
                  <>
                    <Grid item xs={12}>
                      <Divider style={{ margin: '16px 0' }} />
                    </Grid>
                    {!orderCreated && !isLoading &&
                    <Grid item xs={12}>
                      <Typography variant="body1">
                        底下的訂單在重置前已經取消，若要新增請檢視原取消原因後勾選訂單
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedSkippedDates[order.elderId] || false}
                            onChange={handleSelectAll(order.elderId)}
                          />
                        }
                        label="全選"
                      />
                    </Grid>}
                    {order.skippedDatetimes?.map((skipped) => {
                      const matched = createResults.find(({ elderId, deliveryBy }) => elderId === order.elderId && deliveryBy === skipped.datetime);
                      if (!orderCreated && !isLoading) {
                        return (
                          <Grid item sm={4} md={3} lg={2} container align="center" key={skipped.datetime}>
                            <Grid item xs={12} container align="center">
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={skipped.needToCreate}
                                    onChange={(e) => {
                                      skipped.needToCreate = e.target.checked;
                                      setToCreateOrders && setToCreateOrders([...toCreateOrders]);
                                      if (!e.target.checked) {
                                        setSelectedSkippedDates({ ...selectedSkippedDates, [order.elderId]: false });
                                      }
                                    }}
                                  />
                                }
                                label={
                                  <div style={{ marginTop: 2, marginLeft: 4 }}>
                                    <Typography component="span">
                                      {formatDatetime(skipped.datetime, { weekday: true })}
                                    </Typography>
                                    <Typography variant="body2" color="error" component="div">
                                      取消原因：{(cancellationReasons.find(({ value })=> value === skipped.reason) || {}).label || skipped.reason}
                                    </Typography>
                                  </div>
                                }
                              />
                            </Grid>
                          </Grid>
                        );
                      } else {
                        return (
                          <Grid item sm={4} md={3} lg={2} container align="center" key={skipped.datetime}>
                            <Grid item xs={12} container align="center">
                              {isLoading && !matched && <CircularProgress color="primary" size={24} />}
                              {(isLoading || orderCreated) && matched && <CheckIcon color="primary" />}
                              {(!isLoading && orderCreated) && !matched && <CancelIcon color="secondary" />}
                              <Typography style={{ marginTop: 2, marginLeft: 4 }}>
                                {formatDatetime(skipped.datetime, { weekday: true })}
                              </Typography>
                            </Grid>
                          </Grid>
                        );
                      }
                    })}
                  </>
                )}
                <Grid item xs={12}>
                  <Typography variant="h5" component="p" color="secondary">
                    {(createErrors.find(({ elderId })=> elderId === order.elderId) || {}).message || ''}
                  </Typography>
                </Grid>
              </Grid>
            ))
          }
        </Grid>
        <Grid item container xs={12} justifyContent="center">
          {!orderCreated &&
          <Button
            variant="outlined"
            onClick={onCancel}
            style={{ marginRight: 16 }}
            disabled={isLoading}
          >
            取消
          </Button>}
          <Button
            variant="contained"
            color="primary"
            disabled={isLoading}
            onClick={handleComplete}
          >
            {orderCreated ? '關閉' : '確定'}
            {isLoading && <CircularProgress color="primary" size={16} style={{ marginLeft: 8 }} />}
          </Button>
        </Grid>
      </Grid>
    </FormDialog>
  );
}

RecurringOrderUpdateOrdersDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string,
  toCancelOrders: PropTypes.array,
  toCreateOrders: PropTypes.array,
  elders: PropTypes.array,
  toCreateRecurringOrder: PropTypes.object,
  toUpdateRecurringOrder: PropTypes.object,
  toCancelOrdersByServiceEventInfo: PropTypes.array,
  onCancel: PropTypes.func,
  onUpdate: PropTypes.func,
  setToCreateOrders: PropTypes.func,
};
